import { useEffect, useMemo, useState } from "react";

export default function VocabularyFlashcards({
  terms = [],
  getTranslationsForTerm,
  submitVocabularyReview,
  submittingVocabularyReview,
  vocabularyLanguage,
  goBack,
  styles,
}) {
  // =========================================================
  // INITIAL SHUFFLED TERMS
  // =========================================================

  const shuffledTerms = useMemo(() => {
    return [...terms].sort(() => Math.random() - 0.5);
  }, [terms]);

  // =========================================================
  // SESSION STATE
  // =========================================================

  const [sessionQueue, setSessionQueue] = useState([]);
  const [currentTerm, setCurrentTerm] = useState(null);

  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [sessionStats, setSessionStats] = useState({
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
  });

  const [completedTermIds, setCompletedTermIds] = useState([]);

  const [sessionComplete, setSessionComplete] = useState(false);

  // =========================================================
  // START / RESET SESSION
  // =========================================================

  useEffect(() => {
    if (shuffledTerms.length === 0) {
      setSessionQueue([]);
      setCurrentTerm(null);
      setSessionComplete(false);
      return;
    }

    const initialQueue = [...shuffledTerms];

    setCurrentTerm(initialQueue[0]);
    setSessionQueue(initialQueue.slice(1));

    setShowAnswer(false);
    setReviewError("");

    setSessionStats({
      again: 0,
      hard: 0,
      good: 0,
      easy: 0,
    });

    setCompletedTermIds([]);
    setSessionComplete(false);
  }, [shuffledTerms]);

  // =========================================================
  // INSERT TERM BACK INTO QUEUE
  // =========================================================

  const reinsertTerm = (queue, term, minPosition, maxPosition) => {
    const newQueue = [...queue];

    // Prevent accidental duplicate copies of the same term.
    const filteredQueue = newQueue.filter(
      (item) => item.id !== term.id
    );

    if (filteredQueue.length === 0) {
      filteredQueue.push(term);
      return filteredQueue;
    }

    const maxAllowedPosition = Math.min(
      maxPosition,
      filteredQueue.length
    );

    const minAllowedPosition = Math.min(
      minPosition,
      maxAllowedPosition
    );

    const randomPosition =
      Math.floor(
        Math.random() *
          (maxAllowedPosition - minAllowedPosition + 1)
      ) + minAllowedPosition;

    filteredQueue.splice(randomPosition, 0, term);

    return filteredQueue;
  };

  // =========================================================
  // HANDLE REVIEW
  // =========================================================

  const handleReview = async (rating) => {
    if (!currentTerm || submittingVocabularyReview) {
      return;
    }

    setReviewError("");

    // Save review to Supabase first.
    const result = await submitVocabularyReview(
      currentTerm.id,
      rating
    );

    if (!result?.success) {
      setReviewError(
        result?.error ||
          "Unable to save vocabulary review."
      );

      return;
    }

    // Update session statistics.
    setSessionStats((prev) => ({
      ...prev,
      [rating]: prev[rating] + 1,
    }));

    let nextQueue = [...sessionQueue];

    // =======================================================
    // AGAIN
    // Reinsert after approximately 2–3 cards.
    // =======================================================

    if (rating === "again") {
      nextQueue = reinsertTerm(
        nextQueue,
        currentTerm,
        2,
        3
      );
    }

    // =======================================================
    // HARD
    // Reinsert after approximately 5–7 cards.
    // =======================================================

    if (rating === "hard") {
      nextQueue = reinsertTerm(
        nextQueue,
        currentTerm,
        5,
        7
      );
    }

    // =======================================================
    // GOOD / EASY
    // This term passes the current session.
    // =======================================================

    if (rating === "good" || rating === "easy") {
      setCompletedTermIds((prev) => {
        if (prev.includes(currentTerm.id)) {
          return prev;
        }

        return [...prev, currentTerm.id];
      });
    }

    // =======================================================
    // MOVE TO NEXT CARD
    // =======================================================

    if (nextQueue.length === 0) {
      setCurrentTerm(null);
      setSessionQueue([]);
      setShowAnswer(false);
      setSessionComplete(true);
      return;
    }

    const nextTerm = nextQueue[0];

    setCurrentTerm(nextTerm);
    setSessionQueue(nextQueue.slice(1));
    setShowAnswer(false);
  };

  // =========================================================
  // EMPTY STATE
  // =========================================================

  if (terms.length === 0) {
    return (
      <div style={styles.card}>
        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          Back
        </button>

        <div style={styles.emptyBox}>
          No vocabulary available for flashcards.
        </div>
      </div>
    );
  }

  // =========================================================
  // SESSION COMPLETE
  // =========================================================

  if (sessionComplete) {
    return (
      <div style={styles.card}>
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
          }}
        >
          <div
            style={{
              fontSize: "42px",
              marginBottom: "12px",
            }}
          >
            🎉
          </div>

          <h2 style={styles.sectionTitle}>
            Session Complete
          </h2>

          <div
            style={{
              marginTop: "12px",
              color: "#6b7280",
            }}
          >
            You completed all {terms.length} words in this session.
          </div>

          <div
            style={{
              marginTop: "28px",
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(110px, 1fr))",
              gap: "12px",
            }}
          >
            <div style={styles.emptyBox}>
              <strong>Again</strong>
              <div>{sessionStats.again}</div>
            </div>

            <div style={styles.emptyBox}>
              <strong>Hard</strong>
              <div>{sessionStats.hard}</div>
            </div>

            <div style={styles.emptyBox}>
              <strong>Good</strong>
              <div>{sessionStats.good}</div>
            </div>

            <div style={styles.emptyBox}>
              <strong>Easy</strong>
              <div>{sessionStats.easy}</div>
            </div>
          </div>

          <div
            style={{
              marginTop: "20px",
              fontWeight: "600",
            }}
          >
            {completedTermIds.length} of {terms.length} words passed
          </div>

          <button
            style={{
              ...styles.primaryButton,
              marginTop: "28px",
            }}
            onClick={goBack}
          >
            Finish
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // LOADING CURRENT CARD
  // =========================================================

  if (!currentTerm) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyBox}>
          Preparing flashcards...
        </div>
      </div>
    );
  }

  const translations =
    getTranslationsForTerm(currentTerm.id);

  // =========================================================
  // MAIN FLASHCARD UI
  // =========================================================

  return (
    <div style={styles.card}>
      <div style={styles.topBar}>
        <div>
          <h2 style={styles.sectionTitle}>
            Flashcards
          </h2>

          <div style={styles.quizPath}>
            {completedTermIds.length} of {terms.length} words passed
          </div>
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          Back
        </button>
      </div>

      {/* SESSION STATUS */}

      <div
        style={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
          marginTop: "18px",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        <div>
          Remaining:{" "}
          <strong>
            {sessionQueue.length + 1}
          </strong>
        </div>

        <div>
          Again:{" "}
          <strong>{sessionStats.again}</strong>
        </div>

        <div>
          Hard:{" "}
          <strong>{sessionStats.hard}</strong>
        </div>
      </div>

      {/* FLASHCARD */}
      <div
        style={{
          marginTop: "30px",
          perspective: "1200px",
        }}
      >
        <div
          onClick={() =>
            setShowAnswer((prev) => !prev)
          }
          style={{
            position: "relative",
            minHeight: "320px",
            cursor: "pointer",

            transformStyle: "preserve-3d",

            transition:
              "transform 0.55s ease",

            transform: showAnswer
              ? "rotateY(180deg)"
              : "rotateY(0deg)",
          }}
        >
          {/* =====================================================
              FRONT — ENGLISH
          ===================================================== */}

          <div
            style={{
              position: "absolute",
              inset: 0,

              minHeight: "320px",

              border:
                "1px solid #e5e7eb",

              borderRadius: "18px",

              padding: "32px",

              backgroundColor:
                "#ffffff",

              display: "flex",
              flexDirection: "column",

              justifyContent:
                "center",

              alignItems:
                "center",

              textAlign:
                "center",

              backfaceVisibility:
                "hidden",

              WebkitBackfaceVisibility:
                "hidden",

              boxSizing:
                "border-box",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "12px",
              }}
            >
              English
            </div>

            <div
              style={{
                fontSize: "34px",
                fontWeight: "700",
              }}
            >
              {currentTerm.english_term}
            </div>

            <div
              style={{
                marginTop: "24px",
                color: "#6b7280",
              }}
            >
              Click the card to reveal
              the answer
            </div>
          </div>

          {/* =====================================================
              BACK — TRANSLATION
          ===================================================== */}

          <div
            style={{
              position: "absolute",
              inset: 0,

              minHeight: "320px",

              border:
                "1px solid #e5e7eb",

              borderRadius: "18px",

              padding: "32px",

              backgroundColor:
                "#ffffff",

              display: "flex",
              flexDirection: "column",

              justifyContent:
                "center",

              alignItems:
                "center",

              textAlign:
                "center",

              backfaceVisibility:
                "hidden",

              WebkitBackfaceVisibility:
                "hidden",

              transform:
                "rotateY(180deg)",

              boxSizing:
                "border-box",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "12px",
              }}
            >
              Translation
            </div>

            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
              }}
            >
              {
                translations[
                  vocabularyLanguage
                ]
              }
            </div>

            <div
              style={{
                marginTop: "24px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Click the card to flip back
            </div>
          </div>
        </div>
      </div>

      {/* SHOW / HIDE ANSWER */}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <button
          style={styles.smallPrimaryButton}
          onClick={() =>
            setShowAnswer((prev) => !prev)
          }
          disabled={submittingVocabularyReview}
        >
          {showAnswer
            ? "Hide Answer"
            : "Show Answer"}
        </button>
      </div>

      {/* REVIEW BUTTONS */}

      {showAnswer && (
        <div
          style={{
            marginTop: "28px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              fontWeight: "600",
            }}
          >
            How well did you know this word?
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() =>
                handleReview("again")
              }
              disabled={submittingVocabularyReview}
            >
              Again
            </button>

            <button
              onClick={() =>
                handleReview("hard")
              }
              disabled={submittingVocabularyReview}
            >
              Hard
            </button>

            <button
              onClick={() =>
                handleReview("good")
              }
              disabled={submittingVocabularyReview}
            >
              Good
            </button>

            <button
              onClick={() =>
                handleReview("easy")
              }
              disabled={submittingVocabularyReview}
            >
              Easy
            </button>
          </div>

          {submittingVocabularyReview && (
            <div
              style={{
                marginTop: "12px",
                color: "#6b7280",
              }}
            >
              Saving...
            </div>
          )}

          {reviewError && (
            <div
              style={{
                marginTop: "12px",
                color: "#dc2626",
                fontWeight: "600",
              }}
            >
              {reviewError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}